// ─── Helpers compartidos para invocar la Edge Function breeam-analyzer ─────
//
// La Edge Function breeam-analyzer es genérica: recibe { file_infos,
// prompt_text, model, max_tokens } y devuelve el output de Claude por
// streaming SSE. La usa el módulo de Criterios BREEAM (App.js) y el
// Cockpit del Contratista (src/components/CockpitContratista) para
// procesar PDFs largos por rondas de 20 páginas con contexto acumulado.
//
// Extraído de App.js (líneas 3477-3532 y 4048-4069) el 2026-08-08 para
// eliminar la duplicación entre módulos que llaman a la misma función
// Edge con distintos prompts.

/**
 * Invoca breeam-analyzer y acumula el streaming SSE.
 * @param {string} functionUrl · URL completa https://.../functions/v1/breeam-analyzer
 * @param {string} anonKey · REACT_APP_SUPABASE_ANON_KEY
 * @param {object} body · { file_infos, prompt_text, model, max_tokens }
 *   - file_infos: Array<{ name, signed_url, media_type }>
 *   - prompt_text: prompt libre
 *   - model: 'claude-sonnet-4-6' / 'claude-haiku-4-5-20251001'
 *   - max_tokens: 16000 recomendado
 * @returns Promise<{ content: [{ type: 'text', text: string }] }>
 * @throws Error 'STREAM_ERROR:...' si el stream se interrumpe en medio
 * @throws Error con mensaje de timeout si status 524/546/504
 */
export async function callBreeamAnalyzer(functionUrl, anonKey, body) {
  const response = await fetch(functionUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${anonKey}`,
      apikey: anonKey,
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    let apiData = {}
    try { apiData = await response.json() } catch (_) {}
    if (response.status === 546 || response.status === 524 || response.status === 504) {
      throw new Error(
        `La Edge Function ha tardado demasiado en procesar los documentos (error ${response.status}). ` +
        `El batch tiene ${(body.file_infos || []).length} documento(s) — si alguno es muy grande, ` +
        `intenta eliminarlo y re-analiza con menos archivos a la vez.`
      )
    }
    throw new Error(`Error HTTP ${response.status}` + (apiData?.error ? `: ${apiData.error}` : ''))
  }
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('text/event-stream')) {
    // Leer stream SSE y acumular text_delta
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let accumulatedText = ''
    let buffer = ''
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim()
            if (!dataStr || dataStr === '[DONE]') continue
            try {
              const event = JSON.parse(dataStr)
              if (event.type === 'content_block_delta' && event.delta?.type === 'text_delta') {
                accumulatedText += event.delta.text
              } else if (event.error) {
                throw new Error(`Error de la API de Claude: ${event.error.message || JSON.stringify(event.error)}`)
              }
            } catch (parseErr) {
              if (parseErr.message?.startsWith('Error de la API')) throw parseErr
            }
          }
        }
      }
    } catch (streamErr) {
      if (streamErr.message?.toLowerCase().includes('input stream') || streamErr.message?.toLowerCase().includes('network')) {
        throw new Error(
          `STREAM_ERROR: La conexión con la Edge Function se interrumpió durante el análisis. ` +
          `Es posible que el fragmento contenga planos técnicos complejos. Se omite este fragmento y se continúa con el resto.`
        )
      }
      throw streamErr
    }
    return { content: [{ type: 'text', text: accumulatedText }] }
  }
  // Respuesta JSON estándar (fallback)
  return await response.json()
}

/**
 * Parser JSON robusto para outputs de Claude · aplica 5 estrategias
 * de reparación en cascada · devuelve el primer intento que parsee.
 * @param {string} rawText · texto crudo del content block de Claude
 * @returns objeto parseado
 * @throws Error con snippet del texto recibido si todos los intentos fallan
 */
export function parseAiJson(rawText) {
  if (!rawText || !rawText.trim()) throw new Error('La respuesta de Claude está vacía.')

  // Limpieza inicial · quitar bloques markdown ```json``` y espacios
  let cleaned = rawText.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim()

  // Estrategia 1 · JSON.parse directo del texto limpio
  try { return JSON.parse(cleaned) } catch (_) { /* siguiente */ }

  // Estrategia 2 · quitar comas trailing antes de } o ] (frecuentes en outputs Claude)
  const noTrailing = cleaned.replace(/,(\s*[}\]])/g, '$1')
  try { return JSON.parse(noTrailing) } catch (_) { /* siguiente */ }

  // Estrategia 3 · reparar cerrando llaves, corchetes y strings abiertos
  let recovered = noTrailing
  let inString = false
  let escape = false
  let braces = 0
  let brackets = 0
  for (const ch of recovered) {
    if (escape) { escape = false; continue }
    if (ch === '\\') { escape = true; continue }
    if (ch === '"') { inString = !inString; continue }
    if (inString) continue
    if (ch === '{') braces++
    else if (ch === '}') braces--
    else if (ch === '[') brackets++
    else if (ch === ']') brackets--
  }
  if (inString) recovered += '"'
  while (brackets > 0) { recovered += ']'; brackets-- }
  while (braces > 0) { recovered += '}'; braces-- }
  recovered = recovered.replace(/,(\s*[}\]])/g, '$1')
  try { return JSON.parse(recovered) } catch (_) { /* siguiente */ }

  // Estrategia 4 · truncar hasta el último ] o } válido y cerrar el resto
  //   (útil cuando Claude ha alcanzado max_tokens a mitad de un objeto)
  for (let i = noTrailing.length - 1; i > 0; i--) {
    const c = noTrailing[i]
    if (c !== ']' && c !== '}') continue
    let attempt = noTrailing.substring(0, i + 1)
    const openB = (attempt.match(/\{/g) || []).length
    const closeB = (attempt.match(/\}/g) || []).length
    const openS = (attempt.match(/\[/g) || []).length
    const closeS = (attempt.match(/\]/g) || []).length
    for (let k = 0; k < openS - closeS; k++) attempt += ']'
    for (let k = 0; k < openB - closeB; k++) attempt += '}'
    attempt = attempt.replace(/,(\s*[}\]])/g, '$1')
    try { return JSON.parse(attempt) } catch (_) { /* seguir buscando */ }
  }

  // Estrategia 5 · extraer solo lo que hay entre el primer { y el último }
  const firstBrace = cleaned.indexOf('{')
  const lastBrace = cleaned.lastIndexOf('}')
  if (firstBrace !== -1 && lastBrace > firstBrace) {
    const substring = cleaned.substring(firstBrace, lastBrace + 1)
    try { return JSON.parse(substring) } catch (_) { /* fallar con detalle */ }
  }

  // Si llegamos aquí, todas las estrategias fallaron · dar detalle
  const preview = cleaned.length > 300
    ? cleaned.substring(0, 200) + '  [...]  ' + cleaned.substring(cleaned.length - 100)
    : cleaned
  throw new Error(
    `No se pudo parsear la respuesta JSON tras 5 intentos. ` +
    `Longitud del texto: ${rawText.length} chars. ` +
    `Muestra: ${preview}`
  )
}
